import type { AttachmentMimeType, MessageBlock } from "@rakazo/contracts";
import {
  ATTACHMENT_MAX_BYTES,
  attachmentSizeLabel,
  isAllowedAttachmentMimeType,
  isAttachmentImageMimeType,
  isAttachmentOfficeMimeType,
} from "@rakazo/contracts";

/** Attachment the server refuses; the message is shown to the user as is. */
export class AttachmentValidationError extends Error {
  /** Creates the error with a plain-language reason. */
  constructor(message: string) {
    super(message);
    this.name = "AttachmentValidationError";
  }
}

/**
 * Validates base64 without a grouped-repetition regex over the whole string.
 * `/^(?:[A-Za-z0-9+/]{4})*(...)?$/` stack-overflows V8's regex engine on
 * inputs in the megabytes-of-characters range — reproduced directly: a 5 MiB
 * string throws `RangeError: Maximum call stack size exceeded` on `.test()`.
 * At the current 10 MiB attachment limit, base64 expansion (4/3) puts any
 * attachment over ~3.75 MiB squarely in that range, so this crashes on a
 * routine, non-malicious upload. A flat, ungrouped `[chars]*` quantifier does
 * not have this problem; padding is checked separately since it can only
 * ever be the trailing 0-2 characters.
 */
function isWellFormedBase64(value: string): boolean {
  const paddingMatch = /={1,2}$/.exec(value);
  const paddingLength = paddingMatch ? paddingMatch[0].length : 0;
  const body = paddingLength > 0 ? value.slice(0, value.length - paddingLength) : value;
  if (body.includes("=")) return false;
  return /^[A-Za-z0-9+/]*$/.test(body);
}

/** Upload limit in bytes from MAX_UPLOAD_SIZE_MB, or the default when unset. */
export function uploadSizeFromEnv(source: { MAX_UPLOAD_SIZE_MB?: string }): number {
  const value = source.MAX_UPLOAD_SIZE_MB?.trim();
  if (!value) return ATTACHMENT_MAX_BYTES;
  const mb = Number(value);
  // Leave room for base64 expansion and the JSON envelope in the HTTP body guard.
  if (
    !/^\d+$/.test(value) ||
    !Number.isSafeInteger(mb) ||
    mb <= 0 ||
    mb > Math.floor(Number.MAX_SAFE_INTEGER / (2 * 1024 * 1024))
  ) {
    throw new Error(
      "MAX_UPLOAD_SIZE_MB must be a positive integer number of MiB within the safe byte range",
    );
  }
  return mb * 1024 * 1024;
}

/** Decodes attachment base64 and enforces the size limit before and after decoding. */
export function decodeAttachmentBase64(
  contentBase64: string,
  maxBytes = ATTACHMENT_MAX_BYTES,
): Uint8Array {
  const normalized = contentBase64.trim();
  if (!normalized) throw new AttachmentValidationError("Attachment content is empty");
  if (normalized.length > Math.ceil(maxBytes / 3) * 4) {
    throw new AttachmentValidationError(
      `Attachment exceeds the ${attachmentSizeLabel(maxBytes)} limit`,
    );
  }
  if (normalized.length % 4 !== 0 || !isWellFormedBase64(normalized)) {
    throw new AttachmentValidationError("Attachment content is not valid base64");
  }
  let bytes: Buffer;
  try {
    bytes = Buffer.from(normalized, "base64");
  } catch {
    throw new AttachmentValidationError("Attachment content is not valid base64");
  }
  if (bytes.byteLength === 0) throw new AttachmentValidationError("Attachment content is empty");
  if (bytes.byteLength > maxBytes) {
    throw new AttachmentValidationError(
      `Attachment exceeds the ${attachmentSizeLabel(maxBytes)} limit`,
    );
  }
  return new Uint8Array(bytes);
}

/** Throws unless the type is on the attachment allowlist. */
export function validateAttachmentMimeType(mimeType: string): void {
  if (!isAllowedAttachmentMimeType(mimeType)) {
    throw new AttachmentValidationError(`Unsupported attachment type: ${mimeType}`);
  }
}

/**
 * Office formats other than .docx, .xlsx and .pptx: legacy, macro-enabled,
 * template, show and add-in files. Refused by name whatever type is reported.
 */
const BLOCKED_OFFICE_EXTENSIONS = new Set([
  ".doc",
  ".dot",
  ".docm",
  ".dotm",
  ".dotx",
  ".xls",
  ".xlt",
  ".xlsm",
  ".xltm",
  ".xltx",
  ".xlsb",
  ".xlam",
  ".ppt",
  ".pot",
  ".pps",
  ".pptm",
  ".potm",
  ".potx",
  ".ppsm",
  ".ppsx",
  ".ppam",
  ".sldm",
  ".sldx",
]);

/** Message for refused Office files (all formats but .docx, .xlsx, .pptx). */
export const BLOCKED_OFFICE_FILE_MESSAGE =
  "Only .docx, .xlsx and .pptx Office files are supported (no legacy, macro-enabled or " +
  "template formats); save the file as .docx, .xlsx or .pptx";

/**
 * The one name every check and the download use: last path segment, without
 * surrounding white space and trailing dots (Windows drops them on save).
 */
function baseName(name: string): string {
  return (name.split(/[/\\]/).pop() ?? "").trim().replace(/[.\s]+$/, "");
}

/** Lower-case extension of the base name, with its dot, or an empty string. */
function extensionOf(name: string): string {
  const base = baseName(name);
  const dot = base.lastIndexOf(".");
  return dot < 0 ? "" : base.slice(dot).toLowerCase();
}

/** Reason a file name alone rules out an attachment, or null. */
export function attachmentNameRejection(name: string): string | null {
  return BLOCKED_OFFICE_EXTENSIONS.has(extensionOf(name)) ? BLOCKED_OFFICE_FILE_MESSAGE : null;
}

const ZIP_LOCAL_FILE_SIGNATURE = [0x50, 0x4b, 0x03, 0x04];

/**
 * Content check on accepting an attachment (attach_file, upload, inbound).
 * An Office type needs the matching extension and the ZIP signature; neither
 * the reported type nor the name alone is trusted. Other types are unchanged.
 */
export function validateAttachmentContent(input: {
  name: string;
  mimeType: string;
  bytes: Uint8Array;
}): void {
  const nameRejection = attachmentNameRejection(input.name);
  if (nameRejection) throw new AttachmentValidationError(nameRejection);
  const extensionType = EXTENSION_MIME_TYPES[extensionOf(input.name)];
  const officeByName = extensionType !== undefined && isAttachmentOfficeMimeType(extensionType);
  if (!officeByName && !isAttachmentOfficeMimeType(input.mimeType)) return;
  // A name without extension is fine; the download name adds it from the type.
  if (extensionType !== undefined && extensionType !== input.mimeType) {
    throw new AttachmentValidationError(
      `File name ${input.name} does not match attachment type ${input.mimeType}`,
    );
  }
  const signed = ZIP_LOCAL_FILE_SIGNATURE.every((byte, index) => input.bytes[index] === byte);
  if (!signed) {
    throw new AttachmentValidationError(
      `${input.name} is not a valid Office file (no ZIP signature)`,
    );
  }
}

/** Whether an attachment renders as an image or as a file card. */
export function attachmentKindForMimeType(mimeType: string): "image" | "file" {
  return isAttachmentImageMimeType(mimeType) ? "image" : "file";
}

/** Message block (image or file) that shows a stored attachment in the thread. */
export function messageBlockForArtifact(artifact: {
  id: string;
  name: string;
  mimeType: string;
  size: number;
}): Extract<MessageBlock, { kind: "image" | "file" }> {
  if (attachmentKindForMimeType(artifact.mimeType) === "image") {
    return {
      kind: "image",
      artifactId: artifact.id,
      mimeType: artifact.mimeType,
      name: artifact.name,
    };
  }
  return {
    kind: "file",
    artifactId: artifact.id,
    mimeType: artifact.mimeType,
    name: artifact.name,
    size: artifact.size,
  };
}

/** Prompt text for a user turn: the caption plus one note per non-image attachment. */
export function promptTextForAttachments(
  text: string | undefined,
  artifacts: Array<{ name: string; mimeType: string; size: number }>,
): string {
  const caption = text?.trim() ?? "";
  const notes = artifacts
    .filter((artifact) => !isAttachmentImageMimeType(artifact.mimeType))
    .map(
      (artifact) =>
        `User attached file ${JSON.stringify(artifact.name)} (${artifact.mimeType}, ${artifact.size} bytes).`,
    );
  return [caption, ...notes].filter(Boolean).join("\n\n") || "See attached files.";
}

/** Plain-text form of message blocks for the agent's history, with placeholders for media. */
export function blocksToAgentHistoryText(blocks: MessageBlock[]): string {
  return blocks
    .map((block) => {
      if (block.kind === "text") return block.text;
      if (block.kind === "chart") return `[chart: ${block.name}]`;
      if (block.kind === "image") return `[image: ${block.name}]`;
      if (block.kind === "file") {
        return `[file: ${block.name} (${block.mimeType}, ${block.size} bytes)]`;
      }
      // Keep attribution on peer messages: without it a later turn cannot tell
      // which lines came from another bot rather than the user.
      if (block.kind === "bot_message_received") return `[from ${block.fromBotName}] ${block.text}`;
      if (block.kind === "bot_message_sent") return `[to ${block.toBotName}] ${block.text}`;
      if (block.kind === "handoff") {
        return `[handoff ${block.fromBotId} -> ${block.toBotId}] ${block.text}`;
      }
      // Without the answer a later turn sees the question as still open and asks it again.
      if (block.kind === "ask") {
        if (block.status !== "answered" || !block.answer) return block.text;
        const choice = block.actions?.find((action) => action.id === block.answer);
        return `${block.text}\nAnswer: ${choice?.label ?? block.answer}`;
      }
      // The bot needs the outcome of its own call: a missed call is its cue to follow up.
      if (block.kind === "call_request") {
        const outcome = {
          ringing: "still ringing",
          answered: "the user answered",
          declined: "the user declined it",
          missed: "the user did not answer",
        }[block.status];
        return `[you called the user about: ${block.reason}; ${outcome}]${block.status === "answered" ? `\n${block.opening}` : ""}`;
      }
      if ("text" in block && typeof block.text === "string") return block.text;
      return "";
    })
    .filter(Boolean)
    .join("\n");
}

const EXTENSION_MIME_TYPES: Record<string, AttachmentMimeType> = {
  ".mp3": "audio/mpeg",
  ".wav": "audio/wav",
  ".ogg": "audio/ogg",
  ".m4a": "audio/mp4",
  ".mp4": "video/mp4",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".webp": "image/webp",
  ".gif": "image/gif",
  ".pdf": "application/pdf",
  ".txt": "text/plain",
  ".md": "text/markdown",
  ".markdown": "text/markdown",
  ".csv": "text/csv",
  ".html": "text/html",
  ".htm": "text/html",
  ".json": "application/json",
  ".zip": "application/zip",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
};

const MIME_TYPE_EXTENSIONS: Record<AttachmentMimeType, string> = {
  "audio/mpeg": ".mp3",
  "audio/wav": ".wav",
  "audio/ogg": ".ogg",
  "audio/mp4": ".m4a",
  "video/mp4": ".mp4",
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/webp": ".webp",
  "image/gif": ".gif",
  "application/pdf": ".pdf",
  "text/plain": ".txt",
  "text/markdown": ".md",
  "text/csv": ".csv",
  "text/html": ".html",
  "application/json": ".json",
  "application/zip": ".zip",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",
};

/** Allowed type for a file from its name and reported type, or null when it is refused. */
export function inferAttachmentMimeType(
  name: string,
  reportedType?: string,
): AttachmentMimeType | null {
  if (attachmentNameRejection(name)) return null;
  const nameType = EXTENSION_MIME_TYPES[extensionOf(name)];
  // Office types come from the extension: pickers report docx as zip or octet-stream.
  if (nameType && isAttachmentOfficeMimeType(nameType)) return nameType;
  if (reportedType && isAttachmentOfficeMimeType(reportedType)) return nameType ?? reportedType;
  if (
    reportedType === "application/x-zip-compressed" ||
    reportedType === "application/x-zip" ||
    reportedType === "multipart/x-zip"
  ) {
    return "application/zip";
  }
  const audioAliases: Record<string, AttachmentMimeType> = {
    "audio/mp3": "audio/mpeg",
    "audio/x-wav": "audio/wav",
    "audio/wave": "audio/wav",
    "audio/vnd.wave": "audio/wav",
    "audio/x-m4a": "audio/mp4",
  };
  if (reportedType && Object.hasOwn(audioAliases, reportedType)) return audioAliases[reportedType]!;
  // audio/aac may describe an M4A's codec, not its container. Infer from .m4a;
  // do not advertise standalone AAC streams as MP4 containers.
  const extensionType = nameType;
  // Some browsers and native document pickers report Markdown as text/plain.
  if (extensionType === "text/markdown" && (!reportedType || reportedType === "text/plain")) {
    return extensionType;
  }
  if (reportedType && isAllowedAttachmentMimeType(reportedType)) return reportedType;
  return extensionType ?? null;
}

/** File extension for an allowed type, or an empty string. */
export function attachmentExtensionForMimeType(mimeType: string): string {
  return isAllowedAttachmentMimeType(mimeType) ? MIME_TYPE_EXTENSIONS[mimeType] : "";
}

/**
 * File name for a download or share sheet: base name without control characters,
 * at most 80 code points before the extension of its type.
 */
export function attachmentDownloadName(name: string, mimeType: string): string {
  const ext = attachmentExtensionForMimeType(mimeType);
  // Only the last path segment counts: "../geheim/Plan.xlsx" becomes "Plan.xlsx".
  const trimmed = baseName(name);
  const stemSource =
    ext && trimmed.toLowerCase().endsWith(ext.toLowerCase())
      ? trimmed.slice(0, -ext.length)
      : trimmed;
  // Spaces and letters stay (original name); control and reserved characters go.
  const stem = stemSource
    .replace(/[\p{Cc}\p{Cf}\p{Zl}\p{Zp}:*?"<>|]+/gu, "_")
    .replace(/\s+/g, " ")
    .replace(/^[.\s_]+|[.\s_]+$/g, "");
  // 80 code points, not UTF-16 units: a cut never splits a surrogate pair.
  const cut = Array.from(stem).slice(0, 80).join("").trimEnd();
  return `${cut || "attachment"}${ext}`;
}

/** Attachments of one thread; none without a thread key. */
export function attachmentsForThread<T extends { threadKey: string }>(
  attachments: readonly T[],
  threadKey: string | undefined,
): T[] {
  if (!threadKey) return [];
  return attachments.filter((attachment) => attachment.threadKey === threadKey);
}

/** Attachments of one bot; none without a bot id. */
export function attachmentsForBot<T extends { botId: string }>(
  attachments: readonly T[],
  botId: string | undefined,
): T[] {
  if (!botId) return [];
  return attachments.filter((attachment) => attachment.botId === botId);
}

/** User message that started a run, for user-triggered runs only. */
export function userTurnMessageForRun<
  T extends { id?: string; role: string; runId?: string | null; blocks: MessageBlock[] },
>(trigger: string, runId: string, messages: T[], sourceMessageId?: string | null): T | undefined {
  if (trigger !== "user" && trigger !== "messaging" && trigger !== "reaction") return undefined;
  return messages.find(
    (message) =>
      message.role === "user" &&
      (sourceMessageId ? message.id === sourceMessageId : message.runId === runId),
  );
}
